/**
 * 다시 보기의 화면: 아래의 조작 막대(재생·멈춤, 처음으로, 속도, 시간 줄, 볼 부대)와 전장을 끄는 손.
 *
 * - **볼 부대**: 부대를 고르면 카메라가 그 지휘관을 따라간다(`focusUnit`). 적장도 고를 수 있다. 전장의 병사를 눌러도
 *   그 부대를 따라간다.
 * - **자유 카메라**: 전장을 끌면 따라가기를 놓고 그 자리에 선다. 두 손가락으로 벌리거나 휠을 굴리면 배율이 바뀐다.
 *   "전체"는 전장이 다 들어오게 물러난다.
 * - **속도**: 0.5 · 1 · 2 · 4 · 8배. 세운 채로도 카메라는 움직인다.
 * - **시간 줄**: 앞으로는 그 틱까지 빨리 돌리고, 뒤로는 판을 새로 열어 그 틱까지 돌린다(`onRewind`) — 시뮬레이션은
 *   앞으로만 간다.
 * - **주요 장면**(`game/replay-moments.ts`): 시간 줄 위에 눈금으로 찍힌다 — 맞붙은 때, 기술을 쓴 때, 적장·장수가 쓰러진
 *   때, 부대 전멸. 눈금을 누르거나 줄 양옆의 ◂ ▸로 앞뒤 장면에 건너뛴다(조금 앞에서 선다 — 일어나는 것을 보게).
 *   재생이 장면을 지나면 무슨 일인지 알린다(`onMoment`).
 *
 * 싸움의 조작(`BattleControls`·`InputState`)은 붙지 않는다 — 다시 보기는 기록된 커맨드만 넣는다.
 */
import { MOMENT_LEAD, groupMoments, nextMoment, prevMoment, recentMoments } from '../game/replay-moments.ts';
import type { Moment } from '../game/replay-moments.ts';
import { REPLAY_SPEEDS, clockText, focusUnit } from '../game/replay-player.ts';
import type { ReplayFocus, ReplayLoop, ReplayPlayer } from '../game/replay-player.ts';
import type { Renderer } from '../render/renderer.ts';
import { toPx } from '../sim/fixed.ts';
import { TICK_HZ, Team } from '../sim/units.ts';
import { Role } from '../sim/troops.ts';
import { button, el } from './dom.ts';
import { squadName } from './labels.ts';

/** 판을 새로 열 때(되감기) 이어받는 보는 상태. */
export interface ReplayViewState {
  speed: number;
  playing: boolean;
  focus: ReplayFocus;
  /** 배율. null이면 여느 배율(`Renderer.baseZoom`). */
  zoom: number | null;
  /** 카메라의 가운데(월드 픽셀). 자유 카메라로 보던 자리를 잇는다 — 없으면 따라가는 사람에게서 연다. */
  cam: { x: number; y: number } | null;
  /** 속도·볼 곳 줄을 접었는가. 없으면 화면 높이로 정한다. */
  folded?: boolean;
  /**
   * 키보드 초점이 있던 조작(막대 안 단추·시간 줄의 차례). 되감으면 막대를 새로 그리므로 초점을 같은 자리에 돌려준다 —
   * 돌려주지 않으면 Tab으로 간 "−5초"를 연달아 누를 수 없었다(재QA가 짚었다). 초점이 막대 밖이었으면 없다.
   */
  focusAt?: number;
}

export interface ReplayViewDeps {
  /** 조작 막대를 올릴 곳(#overlay). */
  root: HTMLElement;
  /** 전장(#stage). 끌기·벌리기·휠을 여기서 받는다. */
  stage: HTMLElement;
  player: ReplayPlayer;
  loop: ReplayLoop;
  renderer: Renderer;
  init: ReplayViewState;
  /** 이 판의 주요 장면(틱 오름차순). 없으면 눈금도 건너뛰기 단추도 없다. */
  moments: readonly Moment[];
  /** 재생이 장면을 지났다 — 무슨 일인지 알린다. 방금 지난 것들을 함께 준다(오랜 것부터). */
  onMoment: (recent: readonly Moment[]) => void;
  /** 지난 틱으로 되감는다 — 판을 새로 열어야 한다. */
  onRewind: (tick: number, state: ReplayViewState) => void;
  onClose: () => void;
}

/** 이만큼(화면 픽셀) 움직여야 끌기로 친다. 그 안이면 누른 것이다. */
const DRAG_MIN = 6;
/** 누른 자리에서 이 안(화면 픽셀)의 병사를 고른다. */
const TAP_REACH = 28;
const ZOOM_MAX = 3;
/** 화면이 이보다 낮으면(CSS 픽셀, 폰 가로) 속도·볼 곳 줄을 접어서 연다. */
const FOLD_HEIGHT = 480;
/** 이 거리(판 길이의 %) 안의 장면은 한 눈금으로 묶는다. 폭 177px의 시간 줄에서 5%가 9px쯤 — 눈금 하나의 폭이다. */
const MARK_GAP_PCT = 5;
/** 손가락 화면의 묶는 거리(%). 폭 177px의 시간 줄에서 14%가 24px쯤 — 눌리는 자리 하나의 폭이다. */
const MARK_GAP_PCT_TOUCH = 14;
/** 알림에 함께 적는 "방금"의 길이(틱): 2초. */
const RECENT_TICKS = 60;
/** 화살표 키와 건너뛰기 단추가 옮기는 시간(초). */
const SKIP_SECONDS = 5;

function sameFocus(a: ReplayFocus, b: ReplayFocus): boolean {
  if (a.kind !== b.kind) return false;
  return a.kind !== 'squad' || (b.kind === 'squad' && a.squad === b.squad);
}

export class ReplayView {
  private readonly deps: ReplayViewDeps;
  private readonly bar: HTMLElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly clock: HTMLElement;
  private readonly seek: HTMLInputElement;
  private readonly speedBtns: { speed: number; node: HTMLButtonElement }[] = [];
  private readonly focusBtns: { focus: ReplayFocus | 'all'; node: HTMLButtonElement }[] = [];
  private endPanel: HTMLElement | null = null;
  private focus: ReplayFocus;
  private zoom: number;
  /** 속도·볼 곳 줄을 접었는가. 낮은 화면(폰 가로)에서는 접어서 연다 — 펴면 막대가 화면의 4할을 덮었다(QA). */
  private folded: boolean;
  private readonly foldBtn: HTMLButtonElement;
  /** 시간 줄을 쥐고 있는 동안에는 줄을 다시 쓰지 않는다 — 쥔 손잡이가 달아난다. */
  private scrubbing = false;
  private readonly cleanup: (() => void)[] = [];
  private readonly prevBtn: HTMLButtonElement;
  private readonly nextBtn: HTMLButtonElement;
  /** 지난 프레임의 틱. 그 사이에 지난 장면을 알린다. */
  private seenTick: number;
  /** 마지막으로 건너뛰어(또는 판을 열어) 선 틱. 그 앞의 장면은 지나는 것을 보지 못했다 — 알림에 모으지 않는다. */
  private seekFloor: number;
  /** 눈금(가까운 장면의 묶음)의 첫 장면들. ◂ ▸가 이 단위로 건너뛴다. */
  private readonly stops: readonly Moment[];

  constructor(deps: ReplayViewDeps) {
    this.deps = deps;
    const { player, loop, renderer, init } = deps;
    this.focus = init.focus;
    this.seenTick = player.tick;
    this.seekFloor = player.tick;
    this.zoom = init.zoom ?? renderer.baseZoom;
    loop.speed = init.speed;
    loop.playing = init.playing;
    // 되감아 새로 연 판: 보던 자리에서 잇는다. 잇지 않으면 자유 카메라가 전장 구석에서 열렸다.
    if (init.cam) renderer.centerOn(init.cam.x, init.cam.y);

    this.playBtn = button('', () => this.togglePlay(), 'replay-btn replay-play');
    const restart = button('⏮', () => this.seekTo(0), 'replay-btn');
    restart.setAttribute('aria-label', '처음으로');
    restart.title = '처음으로';
    const back = button(`−${SKIP_SECONDS}초`, () => this.skip(-SKIP_SECONDS), 'replay-btn');
    const fwd = button(`+${SKIP_SECONDS}초`, () => this.skip(SKIP_SECONDS), 'replay-btn');
    this.clock = el('span', { class: 'replay-clock', 'aria-live': 'off' });
    const close = button('닫기', () => deps.onClose(), 'replay-btn replay-close');
    this.folded = init.folded ?? window.innerHeight < FOLD_HEIGHT;
    this.foldBtn = button('', () => {
      this.folded = !this.folded;
      this.update();
    }, 'replay-btn replay-fold');

    const speeds = el('div', { class: 'replay-group', role: 'group', 'aria-label': '속도' });
    for (const speed of REPLAY_SPEEDS) {
      const node = button(`${speed}×`, () => this.setSpeed(speed), 'replay-chip');
      this.speedBtns.push({ speed, node });
      speeds.append(node);
    }

    this.seek = el('input', {
      class: 'replay-seek',
      type: 'range',
      min: '0',
      max: String(player.total),
      step: '1',
      'aria-label': '시간',
    });
    this.seek.addEventListener('input', () => {
      this.scrubbing = true;
      this.clock.textContent = `${clockText(Number(this.seek.value))} / ${clockText(player.total)}`;
    });
    this.seek.addEventListener('change', () => {
      this.scrubbing = false;
      this.seekTo(Number(this.seek.value));
    });

    // 주요 장면: 줄 위의 눈금과 양옆의 건너뛰기. 눈금은 Tab 차례에서 뺀다(수십 개일 수 있다) — 키보드는 ◂ ▸와 [ ]로 간다.
    this.prevBtn = button('◂', () => this.jumpMoment(-1), 'replay-btn replay-scene');
    this.prevBtn.setAttribute('aria-label', '앞 장면으로');
    this.prevBtn.title = '앞 장면으로 ( [ )';
    this.nextBtn = button('▸', () => this.jumpMoment(1), 'replay-btn replay-scene');
    this.nextBtn.setAttribute('aria-label', '다음 장면으로');
    this.nextBtn.title = '다음 장면으로 ( ] )';
    const marks = el('div', { class: 'replay-marks', 'aria-hidden': 'true' });
    // 가까운 장면은 한 눈금으로 묶는다(`groupMoments`) — 줄 위에서 눌리는 자리가 포개지는 거리 안이면 한 눈금이다.
    // 손가락 화면은 눌리는 자리가 넓어(24px) 더 멀리까지 묶는다: 5%로만 묶었더니 폰 360에서 30틱 떨어진 "장수가
    // 쓰러졌다"가 뒤의 전멸 눈금에 가려 눌리지 않았다(재QA가 쟀다).
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const groups = groupMoments(deps.moments, Math.max(1, Math.round((player.total * (coarse ? MARK_GAP_PCT_TOUCH : MARK_GAP_PCT)) / 100)));
    this.stops = groups.map((g) => g.moments[0]);
    for (const g of groups) {
      const mark = button('', () => this.seekTo(g.tick - MOMENT_LEAD), `replay-mark ${g.kind}${g.moments.length > 1 ? ' many' : ''}`);
      mark.tabIndex = -1;
      mark.title = g.moments.map((m) => `${clockText(m.tick)} ${m.label}`).join('\n');
      mark.style.left = `${player.total > 0 ? (g.tick * 100) / player.total : 0}%`;
      marks.append(mark);
    }
    const track = el('div', { class: 'replay-track' }, marks, this.seek);
    const hasMoments = deps.moments.length > 0;

    const foci = el('div', { class: 'replay-group replay-foci', role: 'group', 'aria-label': '카메라가 볼 곳' });
    const addFocus = (label: string, focus: ReplayFocus | 'all', title: string): void => {
      const node = button(label, () => this.pickFocus(focus), 'replay-chip');
      node.title = title;
      this.focusBtns.push({ focus, node });
      foci.append(node);
    };
    const w = player.world;
    for (let sq = 0; sq < w.sqUsed.length; sq++) {
      if (w.sqUsed[sq]) addFocus(squadName(sq), { kind: 'squad', squad: sq }, `${squadName(sq)}를 따라간다`);
    }
    if (w.chiefIdx >= 0) addFocus('적장', { kind: 'chief' }, '적장을 따라간다');
    addFocus('자유', { kind: 'free' }, '카메라를 그 자리에 세운다 — 전장을 끌어 옮긴다');
    addFocus('전체', 'all', '전장이 다 보이게 물러난다');

    this.bar = el(
      'div',
      { class: 'hud replay-bar' },
      // 시계는 시간 줄 곁에 둔다. 처음에는 단추 줄 가운데였는데, 접기 단추가 들어오자 폭 360에서 닫기 단추가 막대
      // 밖으로 밀렸다(리뷰가 쟀다: 오른쪽 끝 366px, 막대는 352px).
      el('div', { class: 'replay-row' }, this.playBtn, restart, back, fwd, this.foldBtn, close),
      el('div', { class: 'replay-row' }, hasMoments ? this.prevBtn : null, track, hasMoments ? this.nextBtn : null, this.clock),
      el('div', { class: 'replay-row replay-more' }, speeds),
      el('div', { class: 'replay-row replay-more' }, foci),
    );
    // 마우스·손가락으로 누른 단추에 초점이 남으면 단축키가 그 단추의 것이 된다(스페이스가 방금 누른 "2×"를 또 누른다) —
    // 처음에는 그렇게 남아, 단추를 한 번 누른 뒤로는 Space·화살표·숫자가 전부 먹지 않았다(QA가 짚었다). 누른 뒤 초점을
    // 푼다. Tab으로 옮긴 초점은 그대로다(눌림이 아니다).
    this.bar.addEventListener('pointerup', () => {
      queueMicrotask(() => {
        const a = document.activeElement;
        // 시간 줄도 같다 — 끌어 놓은 뒤 초점이 남으면 Space와 숫자가 먹지 않았다(재QA).
        if ((a instanceof HTMLButtonElement || a instanceof HTMLInputElement) && this.bar.contains(a)) a.blur();
      });
    });
    deps.root.append(this.bar);

    this.attachStage();
    this.attachKeys();
    this.update();
    if (init.focusAt !== undefined) this.controls()[init.focusAt]?.focus();
  }

  /** 막대 안의 조작(단추·시간 줄), 문서 순서대로. 접어서 숨긴 것도 센다 — 차례가 접기와 무관하게 같아야 한다. */
  private controls(): HTMLElement[] {
    return [...this.bar.querySelectorAll<HTMLElement>('button, input')];
  }

  /** 되감아 새로 열 때 이어받을 상태. */
  state(): ReplayViewState {
    const { loop, renderer } = this.deps;
    const at = document.activeElement instanceof HTMLElement ? this.controls().indexOf(document.activeElement) : -1;
    return {
      speed: loop.speed,
      playing: loop.playing,
      focus: this.focus,
      zoom: this.zoom,
      cam: renderer.camCenter(),
      folded: this.folded,
      focusAt: at >= 0 ? at : undefined,
    };
  }

  private togglePlay(): void {
    const { loop, player } = this.deps;
    // 끝난 판에서 재생을 누르면 처음부터 다시.
    if (player.done) {
      this.deps.onRewind(0, { ...this.state(), playing: true });
      return;
    }
    loop.playing = !loop.playing;
    this.update();
  }

  private setSpeed(speed: number): void {
    this.deps.loop.speed = speed;
    this.update();
  }

  private stepSpeed(dir: number): void {
    const i = REPLAY_SPEEDS.indexOf(this.deps.loop.speed);
    const next = REPLAY_SPEEDS[Math.min(REPLAY_SPEEDS.length - 1, Math.max(0, (i < 0 ? 1 : i) + dir))];
    this.setSpeed(next);
  }

  private skip(seconds: number): void {
    this.seekTo(this.deps.player.tick + seconds * TICK_HZ);
  }

  private seekTo(tick: number): void {
    const { player } = this.deps;
    const to = Math.min(player.total, Math.max(0, Math.round(tick)));
    if (to >= player.tick) {
      player.seek(to);
      // 건너뛴 구간의 장면은 알리지 않는다 — 본 것으로 친다. 처음에는 한 프레임에 나아간 틱 수로 재생과 건너뛰기를
      // 갈랐는데, 짧게 건너뛰면(60틱 안) 재생으로 쳐서 지나친 장면을 알렸다(리뷰가 재현했다).
      this.seenTick = player.tick;
      this.seekFloor = player.tick;
      this.update();
    } else {
      this.deps.onRewind(to, this.state());
    }
  }

  /** 앞(-1)·다음(+1) 장면으로. 그 장면 조금 앞에 선다. */
  private jumpMoment(dir: number): void {
    const { player } = this.deps;
    // 눈금 단위로 건너뛴다 — 장면 하나씩이면 한 눈금으로 묶인 셋을 지나는 데 세 번을 눌러야 했고, 그 사이는 2~10틱
    // 뿐이었다(재QA가 짚었다).
    const moments = this.stops;
    // 재생 중의 "앞 장면"은 방금 건넌 장면을 건너 그 앞으로 간다(여유를 둔다). 그 앞이 없으면 방금 그 장면을 다시 본다 —
    // 처음에는 아무 일도 하지 않아, 단추는 눌리는데 반응이 없는 순간이 있었다(QA가 짚었다).
    const m =
      dir > 0
        ? nextMoment(moments, player.tick)
        : (prevMoment(moments, player.tick, this.deps.loop.playing ? 30 : 0) ?? prevMoment(moments, player.tick, 0));
    if (m) this.seekTo(m.tick - MOMENT_LEAD);
  }

  private pickFocus(focus: ReplayFocus | 'all'): void {
    const { renderer } = this.deps;
    if (focus === 'all') {
      this.focus = { kind: 'free' };
      this.zoom = renderer.fitZoom();
      renderer.centerOnField();
    } else {
      this.focus = focus;
      // "전체"로 물러나 있었으면 따라갈 때 여느 배율로 돌아온다 — 물러난 채로는 누구를 따라가는지 안 보인다.
      if (focus.kind !== 'free' && this.zoom < renderer.baseZoom * 0.8) this.zoom = renderer.baseZoom;
    }
    this.update();
  }

  private zoomBy(factor: number): void {
    const { renderer } = this.deps;
    const min = Math.min(renderer.fitZoom(), renderer.baseZoom);
    this.zoom = Math.min(ZOOM_MAX, Math.max(min, this.zoom * factor));
  }

  /** 누른 자리 가까이의 병사 — 우리 병사면 그 부대를, 적장이면 적장을 따라간다. */
  private tap(sx: number, sy: number): void {
    const { renderer, player } = this.deps;
    const w = player.world;
    const at = renderer.screenToWorld(sx, sy);
    const reach = TAP_REACH / this.zoom;
    let best = -1;
    let bestD = reach * reach;
    for (let i = 0; i < w.count; i++) {
      if (w.alive[i] !== 1 || w.exited[i] !== 0) continue;
      const pick = w.team[i] === Team.Player || w.role[i] === Role.Chief;
      if (!pick) continue;
      const dx = toPx(w.x[i]) - at.x;
      const dy = toPx(w.y[i]) - at.y;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    if (best < 0) return;
    this.pickFocus(w.team[best] === Team.Player ? { kind: 'squad', squad: w.squad[best] } : { kind: 'chief' });
  }

  private attachStage(): void {
    const { stage, renderer } = this.deps;
    const on = (target: EventTarget, type: string, fn: (e: never) => void, opts?: AddEventListenerOptions): void => {
      target.addEventListener(type, fn as EventListener, opts);
      this.cleanup.push(() => target.removeEventListener(type, fn as EventListener, opts));
    };
    const pts = new Map<number, { x: number; y: number }>();
    let moved = 0;
    let dragged = false;
    const spread = (): number => {
      const [a, b] = [...pts.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    on(stage, 'pointerdown', (e: PointerEvent) => {
      if (pts.size === 0) {
        moved = 0;
        dragged = false;
      }
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      stage.setPointerCapture(e.pointerId);
    });
    on(stage, 'pointermove', (e: PointerEvent) => {
      const p = pts.get(e.pointerId);
      if (!p) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      const before = pts.size === 2 ? spread() : 0;
      p.x = e.clientX;
      p.y = e.clientY;
      moved += Math.abs(dx) + Math.abs(dy);
      if (!dragged && moved < DRAG_MIN && pts.size === 1) return;
      if (!dragged) {
        dragged = true;
        // 끌기 시작: 따라가기를 놓는다. 놓지 않으면 끈 만큼 카메라가 도로 끌려간다.
        if (this.focus.kind !== 'free') {
          this.focus = { kind: 'free' };
          this.update();
        }
      }
      // 두 손가락이면 한 손가락의 움직임을 반만 싣는다 — 둘이 같이 움직이면 가운데가 그만큼 간다.
      const k = pts.size === 2 ? 0.5 : 1;
      renderer.panBy(dx * k, dy * k);
      if (pts.size === 2 && before > 0) this.zoomBy(spread() / before);
    });
    const end = (e: PointerEvent): void => {
      if (!pts.delete(e.pointerId)) return;
      if (e.type === 'pointerup' && !dragged && pts.size === 0) {
        const r = stage.getBoundingClientRect();
        this.tap(e.clientX - r.left, e.clientY - r.top);
      }
    };
    on(stage, 'pointerup', end);
    on(stage, 'pointercancel', end);
    on(
      stage,
      'wheel',
      (e: WheelEvent) => {
        e.preventDefault();
        this.zoomBy(Math.exp(-e.deltaY * 0.0015));
      },
      { passive: false },
    );
  }

  private attachKeys(): void {
    const key = (e: KeyboardEvent): void => {
      // 시간 줄·단추에 초점이 있을 때의 스페이스·화살표는 그 요소의 것이다.
      const t = e.target;
      if (t instanceof HTMLInputElement || t instanceof HTMLButtonElement) {
        if (e.code === 'Escape') this.deps.onClose();
        return;
      }
      const digit = /^Digit([1-9])$/.exec(e.code);
      if (digit) {
        const sq = Number(digit[1]) - 1;
        if (this.deps.player.world.sqUsed[sq]) this.pickFocus({ kind: 'squad', squad: sq });
        return;
      }
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          this.togglePlay();
          break;
        case 'ArrowLeft':
          this.skip(-SKIP_SECONDS);
          break;
        case 'ArrowRight':
          this.skip(SKIP_SECONDS);
          break;
        case 'ArrowUp':
        case 'Equal':
          this.stepSpeed(1);
          break;
        case 'ArrowDown':
        case 'Minus':
          this.stepSpeed(-1);
          break;
        case 'Digit0':
          this.pickFocus('all');
          break;
        case 'BracketLeft':
          this.jumpMoment(-1);
          break;
        case 'BracketRight':
          this.jumpMoment(1);
          break;
        case 'Escape':
          this.deps.onClose();
          break;
      }
    };
    window.addEventListener('keydown', key);
    this.cleanup.push(() => window.removeEventListener('keydown', key));
  }

  /** 매 프레임: 카메라가 따라갈 사람을 다시 잡고(지휘관이 쓰러지면 바뀐다), 막대를 지금 상태로 쓴다. */
  update(): void {
    const { player, loop, renderer } = this.deps;
    let unit = focusUnit(player.world, this.focus);
    // 따라가던 부대가 전멸했다(또는 적장이 쓰러졌다): 자유로 놓는다. 그대로 두면 단추는 그 부대를 가리키는데 카메라는
    // 빈 땅을 비췄다(QA가 짚었다). 카메라는 마지막 자리에 선다.
    if (unit < 0 && this.focus.kind !== 'free') {
      this.focus = { kind: 'free' };
      unit = -1;
    }
    renderer.spectate = { unit, zoom: this.zoom };
    this.bar.classList.toggle('folded', this.folded);
    const foldLabel = this.folded ? '속도·볼 곳 펴기' : '속도·볼 곳 접기';
    if (this.foldBtn.title !== foldLabel) {
      this.foldBtn.textContent = this.folded ? '▴' : '▾';
      this.foldBtn.title = foldLabel;
      this.foldBtn.setAttribute('aria-label', foldLabel);
      this.foldBtn.setAttribute('aria-expanded', String(!this.folded));
    }

    // 지난 프레임 뒤로 지난 장면을 알린다 — 재생으로 지났을 때만. 건너뛴 구간은 `seekTo`가 본 것으로 쳐 둔다: 처음에는
    // 알렸더니 적장이 쓰러지는 장면 앞으로 건너뛰었는데 한참 전의 "맞붙었다"가 떴다. 한 프레임에 여럿이면 마지막 것만.
    if (player.tick > this.seenTick) {
      const passed = this.deps.moments.some((m) => m.tick > this.seenTick && m.tick <= player.tick);
      // 방금 지난 것들을 함께 알린다 — 일이 몰리면 앞의 알림이 읽기도 전에 덮였다(`recentMoments`).
      if (passed) this.deps.onMoment(recentMoments(this.deps.moments, player.tick, RECENT_TICKS, this.seekFloor));
    }
    this.seenTick = player.tick;
    const hasNext = nextMoment(this.stops, player.tick) !== null;
    const hasPrev = prevMoment(this.stops, player.tick, 0) !== null;
    if (this.nextBtn.disabled === hasNext) this.nextBtn.disabled = !hasNext;
    if (this.prevBtn.disabled === hasPrev) this.prevBtn.disabled = !hasPrev;

    const playing = loop.playing && !player.done;
    const label = player.done ? '처음부터' : playing ? '멈춤' : '재생';
    const glyph = player.done ? '↺' : playing ? '⏸' : '▶';
    if (this.playBtn.textContent !== glyph) {
      this.playBtn.textContent = glyph;
      this.playBtn.setAttribute('aria-label', label);
      this.playBtn.title = label;
    }
    if (!this.scrubbing) {
      // 접은 막대에는 지금 속도와 보는 곳을 시계 곁에 적는다 — 칩이 숨어 무엇을 보는 중인지 알 수 없었다(재QA).
      const now = this.focusBtns.find((f) => f.focus !== 'all' && sameFocus(f.focus, this.focus))?.node.textContent ?? '';
      const text = `${clockText(player.tick)} / ${clockText(player.total)}${this.folded ? ` · ${loop.speed}× · ${now}` : ''}`;
      if (this.clock.textContent !== text) this.clock.textContent = text;
      const v = String(player.tick);
      if (this.seek.value !== v) this.seek.value = v;
    }
    for (const s of this.speedBtns) {
      const on = s.speed === loop.speed;
      s.node.classList.toggle('on', on);
      s.node.setAttribute('aria-pressed', String(on));
    }
    for (const f of this.focusBtns) {
      const on = f.focus !== 'all' && sameFocus(f.focus, this.focus);
      f.node.classList.toggle('on', on);
      f.node.setAttribute('aria-pressed', String(on));
      // 따라갈 사람이 남지 않은 부대(전멸)·쓰러진 적장은 고를 수 없다.
      const gone = f.focus !== 'all' && f.focus.kind !== 'free' && focusUnit(player.world, f.focus) < 0;
      if (f.node.disabled !== gone) f.node.disabled = gone;
    }
  }

  /** 끝까지 돌았다: 가운데에 알리고 처음부터 볼지 닫을지 묻는다. 카메라는 그대로 움직인다. */
  showEnd(): void {
    if (this.endPanel) return;
    this.endPanel = el(
      'div',
      { class: 'hud hud-end replay-end' },
      el('b', {}, '다시 보기 끝'),
      button('처음부터', () => this.deps.onRewind(0, { ...this.state(), playing: true })),
      button('닫기', () => this.deps.onClose(), 'primary'),
    );
    this.deps.root.append(this.endPanel);
  }

  detach(): void {
    for (const fn of this.cleanup) fn();
    this.cleanup.length = 0;
    this.bar.remove();
    this.endPanel?.remove();
  }
}
